import { Component, Input, Output, EventEmitter, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MultiSchedulerData } from '@app/models';
import { Timezone } from '@app/models/organization.model';
import { SchedulerFieldsComponent } from '../scheduler-fields/scheduler-fields.component';
import * as moment from 'moment-timezone';
import { BROADCAST_MESSAGES, GENERAL_MESSAGES, SURVEY_MESSAGES } from '@app/shared/data/messages';
import { BroadcastStatus, DATE_TIME_FORMATS } from '@app/shared/data/values';

interface ScheduleValues {
  createdBy: string;
  value: string;
  status: {
    class: string;
    value: string;
  } | null;
  disabled?: boolean;
};
@Component({
    selector: 'app-multi-scheduler',
    templateUrl: './multi-scheduler.component.html',
    styleUrls: ['./multi-scheduler.component.scss'],
    standalone: false
})
export class MultiSchedulerComponent implements OnInit {

  generalMessages = GENERAL_MESSAGES;
  dateTimeFormats = DATE_TIME_FORMATS;
  @Input('schedulersData') schedulersData: MultiSchedulerData | null = null;
  @Input('timeZoneData') timeZoneData!: Timezone;
  @Input('timeIntervalMinutes') timeIntervalMinutes: number = 15;
  @Output('valueChanges') valueChanges: EventEmitter<any> = new EventEmitter<any>();
  @Output('rerunAdded') rerunAdded: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('sheetTemp') sheetTemp!: TemplateRef<any>;
  @ViewChild('scheduleFieldsCompRef') scheduleFieldsCompRef!: SchedulerFieldsComponent;
  scheduleValues: ScheduleValues[] = [];
  formErrors: { [k: string]: string; } = {};

  constructor(private readonly _bottomSheet: MatBottomSheet) { }

  openSheet(): void {
    this._bottomSheet.open(this.sheetTemp, { panelClass: 'mr-70', disableClose: true });
  }

  ngOnInit(): void {
    this.initiateValues();
  }

  initiateValues(data: MultiSchedulerData | null = this.schedulersData) {
    this.scheduleValues = data?.values || [];
    this.sortScheduleValues();
    this.valueChanges.emit();
  }

  addSchedule() {
    this.valueChanges.emit();
    const value = this.scheduleFieldsCompRef.getCombineDateTime().format();
    if (this.isInvalid(value)) {
      this.clearErrors('duplicateTime');
      this.formErrors['inValidTime'] = BROADCAST_MESSAGES.SCHEDULER_FORM.RERUN_WRONG_DATETIME;
      return;
    } else if (this.isDuplicate(value)) {
      this.clearErrors('inValidTime');
      this.formErrors['duplicateTime'] = SURVEY_MESSAGES.EXECUTION.MULTI_SCH_DUPLICATE;
      return;
    } else {
      this.clearErrors();
    }


    this.scheduleValues = [
      ...this.scheduleValues,
      {
        createdBy: this.schedulersData?.userMail || '',
        value: value,
        status: BroadcastStatus.draft
      }
    ];
    this.sortScheduleValues();
    this.closeForm();
    // Emit rerunAdded event only when rerun is successfully added
    this.rerunAdded.emit();
  }

  removeSchedule(value: string) {
    this.scheduleValues = this.scheduleValues.filter(schValue => schValue.value != value);
    this.closeForm();
    this.valueChanges.emit();
  }

  closeForm() {
    this._bottomSheet.dismiss();
    this.clearErrors();
    this.scheduleFieldsCompRef?.createForm();
    this.valueChanges.emit();
  }

  clearErrors(errorKey?: string) {
    if (errorKey?.length && this.formErrors.hasOwnProperty(errorKey)) {
      this.formErrors[errorKey] = '';
    } else {
      this.formErrors = {};
    }
  }

  private sortScheduleValues() {
    if (!this.scheduleValues?.length) return;

    
    this.scheduleValues = [...this.scheduleValues].sort((current, next) => {
      let isAfterValue = (moment.utc(current.value).isAfter(moment.utc(next.value)) ? 1 : 0);

      return (moment.utc(current.value).isBefore(moment.utc(next.value)) ? -1 : isAfterValue);
    });
  }

  private isInvalid(value: string): boolean {
    return moment.utc(value).isBefore(moment.utc())
      || ((!!this.schedulersData?.minScheduleTime?.length)
        && moment.utc(value).isBefore(moment.utc(this.schedulersData.minScheduleTime)));
  }

  private isDuplicate(value: string): boolean {
    return (!!this.scheduleValues.find(schValue => moment.utc(schValue.value).isSame(moment.utc(value))))
      || ((!!this.schedulersData?.minScheduleTime?.length)
        && moment.utc(this.schedulersData.minScheduleTime).isSame(moment.utc(value)));
  }

}
